import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, Plus, RotateCcw, Move } from 'lucide-react';

// Larghezza/altezza A4 in px @96dpi (coerente con il rendering del PDF a 210mm).
const A4_W = 794;
const A4_H = 1123;
const KEYS = ['logo', 'name', 'address', 'phone'];

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

/**
 * Editor interattivo della copertina.
 * - Trascina un elemento per spostarlo.
 * - Trascina la maniglia in basso a destra per ridimensionarlo.
 * - Pulsante X per rimuoverlo; barra in alto per ripristinare gli elementi rimossi.
 *
 * props:
 *  - spec: { logo|name|address|phone: { html, wrap } | null }
 *  - value: coverEl { key: { visible, x, y, scale } }
 *  - onChange(next)
 *  - bg: { image?: string, color: string }
 *  - width: larghezza display in px
 *  - labels: { restore, reset, hint, names: {logo,name,address,phone} }
 */
export const CoverEditor = ({ spec, value, onChange, bg, width, labels, keys }) => {
  const wrapRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const drag = useRef(null);
  const scale = width / A4_W;
  const dispH = A4_H * scale;
  const elemKeys = keys || Object.keys(value || {});

  const update = useCallback((key, patch) => {
    onChange({ ...value, [key]: { ...value[key], ...patch } });
  }, [value, onChange]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    const rect = wrapRef.current.getBoundingClientRect();
    if (d.mode === 'move') {
      const dxPct = ((e.clientX - d.startX) / rect.width) * 100;
      const dyPct = ((e.clientY - d.startY) / rect.height) * 100;
      update(d.key, { x: clamp(d.ox + dxPct, 3, 97), y: clamp(d.oy + dyPct, 3, 97) });
    } else if (d.mode === 'resize') {
      const dy = e.clientY - d.startY;
      update(d.key, { scale: clamp(d.os + dy / 140, 0.4, 3.2) });
    }
  }, [update]);

  const endDrag = useCallback(() => { drag.current = null; }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
    };
  }, [onPointerMove, endDrag]);

  const startMove = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    setSelected(key);
    drag.current = { mode: 'move', key, startX: e.clientX, startY: e.clientY, ox: value[key].x, oy: value[key].y };
  };
  const startResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    setSelected(key);
    drag.current = { mode: 'resize', key, startY: e.clientY, os: value[key].scale };
  };

  const hidden = elemKeys.filter((k) => spec[k] && !value[k]?.visible);

  return (
    <div className="space-y-2" data-testid="cover-editor">
      {/* Barra strumenti: ripristina elementi rimossi + reset */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground flex items-center gap-1"><Move className="h-3.5 w-3.5" /> {labels.hint}</span>
        <span className="flex-1" />
        {hidden.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => update(k, { visible: true })}
            className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 hover:bg-accent transition-colors"
            data-testid={`cover-restore-${k}`}
          >
            <Plus className="h-3 w-3" /> {labels.names[k]}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(null)}
          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 hover:bg-accent transition-colors"
          data-testid="cover-reset"
        >
          <RotateCcw className="h-3 w-3" /> {labels.reset}
        </button>
      </div>

      {/* Canvas copertina (A4 scalato) */}
      <div
        ref={wrapRef}
        onPointerDown={() => setSelected(null)}
        className="relative mx-auto overflow-hidden rounded-lg shadow-sm ring-1 ring-black/10 select-none"
        style={{
          width, height: dispH,
          backgroundColor: bg.color || '#fff',
          backgroundImage: bg.image ? `url(${bg.image})` : 'none',
          backgroundSize: 'cover', backgroundPosition: 'center',
          touchAction: 'none', cursor: 'default',
        }}
        data-testid="cover-editor-canvas"
      >
        {/* Contenitore alla scala A4 reale, poi ridotto: i font in px combaciano col PDF */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: A4_W, height: A4_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {elemKeys.map((key) => {
            const sp = spec[key];
            const cfg = value[key];
            if (!sp || !cfg?.visible) return null;
            const isSel = selected === key;
            return (
              <div
                key={key}
                onPointerDown={(e) => startMove(e, key)}
                style={{
                  position: 'absolute', left: `${cfg.x}%`, top: `${cfg.y}%`,
                  transform: `translate(-50%, -50%) scale(${cfg.scale})`, transformOrigin: 'center',
                  maxWidth: '86%', textAlign: 'center', cursor: 'move',
                  outline: isSel ? '2px dashed rgba(59,130,246,.9)' : '1px dashed rgba(0,0,0,.18)',
                  outlineOffset: 6,
                }}
                data-testid={`cover-el-${key}`}
              >
                <div style={{ pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: `<div style="${sp.wrap}">${sp.html}</div>` }} />
                {/* Pulsante rimuovi */}
                <div
                  onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); update(key, { visible: false }); if (selected === key) setSelected(null); }}
                  title={labels.remove}
                  style={{
                    position: 'absolute', top: -14, right: -14, width: 26, height: 26, borderRadius: '50%',
                    background: '#ef4444', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,.3)',
                  }}
                  data-testid={`cover-remove-${key}`}
                >
                  <X size={16} />
                </div>
                {/* Maniglia ridimensiona */}
                <div
                  onPointerDown={(e) => startResize(e, key)}
                  title={labels.resize}
                  style={{
                    position: 'absolute', bottom: -14, right: -14, width: 24, height: 24, borderRadius: 6,
                    background: '#3b82f6', border: '2px solid #fff', cursor: 'nwse-resize',
                    boxShadow: '0 1px 4px rgba(0,0,0,.3)',
                  }}
                  data-testid={`cover-resize-${key}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CoverEditor;

// Renderizza HTML statico (pagine menù + ringraziamento) a larghezza A4 reale,
// scalato per adattarsi all'anteprima. Non interattivo.
export const ScaledSheet = ({ html, width, bgColor }) => {
  const innerRef = useRef(null);
  const [h, setH] = useState(0);
  const scale = width / A4_W;
  useLayoutEffect(() => {
    if (innerRef.current) setH(innerRef.current.scrollHeight * scale);
  }, [html, width, scale]);
  return (
    <div style={{ width, height: h, position: 'relative', margin: '0 auto' }} data-testid="preview-static-pages">
      <div
        ref={innerRef}
        style={{ position: 'absolute', top: 0, left: 0, width: A4_W, transform: `scale(${scale})`, transformOrigin: 'top left', backgroundColor: bgColor }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};
